"use client";

import * as React from "react";
import { cn } from "@/src/lib/utils";

export interface MobileTab {
  value: string;
  label: React.ReactNode;
}

interface MobileTabChipsProps {
  tabs: MobileTab[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

export function MobileTabChips({
  tabs,
  value,
  onValueChange,
  className,
}: MobileTabChipsProps) {
  const chipRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  React.useEffect(() => {
    chipRefs.current[value]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [value]);

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        "scrollbar-hide -mx-1 flex shrink-0 snap-x snap-mandatory gap-2 overflow-x-auto px-1 py-1",
        className
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              chipRefs.current[tab.value] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onValueChange(tab.value)}
            className={cn(
              "shrink-0 snap-center rounded-full border px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
              isActive
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-transparent bg-muted text-muted-foreground active:bg-muted/70"
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function useSwipeTabs(
  tabs: { value: string }[],
  value: string,
  onValueChange: (value: string) => void,
  enabled: boolean
) {
  const start = React.useRef<{ x: number; y: number } | null>(null);
  const axis = React.useRef<"h" | "v" | null>(null);

  if (!enabled) return {};

  return {
    onTouchStart: (e: React.TouchEvent) => {
      if ((e.target as HTMLElement).closest("[data-no-tab-swipe]")) {
        start.current = null;
        axis.current = null;
        return;
      }
      const t = e.touches[0];
      start.current = { x: t.clientX, y: t.clientY };
      axis.current = null;
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (!start.current || axis.current) return;
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - start.current.x);
      const dy = Math.abs(t.clientY - start.current.y);
      if (dx > 12 || dy > 12) axis.current = dx > dy * 1.5 ? "h" : "v";
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const origin = start.current;
      start.current = null;
      if (!origin || axis.current !== "h") return;
      const dx = e.changedTouches[0].clientX - origin.x;
      if (Math.abs(dx) < 60) return;
      const index = tabs.findIndex((tab) => tab.value === value);
      if (index === -1) return;
      const next = dx < 0 ? index + 1 : index - 1;
      if (next >= 0 && next < tabs.length) onValueChange(tabs[next].value);
    },
  };
}
